import React, { useEffect, useMemo, useState } from 'react';
import { useApacuana } from 'contexts/ApacuanaContext';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps, Link } from 'react-router-dom';
import { Constants, Globals } from 'utils';
import { BackIcon } from 'assets/icons';
import './index.scss';
import RequestCertForm from '../components/infoCertificate';
import BasicDataCertificate from '../components/BasicDataCertificate';
import UploadDocsCertificate from '../components/UploadDocsCertificate';
import FaceLiveness from '../components/FaceLiveness';
import { CERTIFICATES_TYPES, MEDIAPIPE_URL } from '../utils';
import { AuthService, UserService } from 'services';
import { store } from 'store';

interface Props extends RootState, RouteComponentProps {
  dispatch: any;
  apacuana?: any;
  cert: any;
  step: number;
  handleNext: (data?: any) => void;
  handleBack: () => void;
  createApacuanaUser: (data: any) => void;
  uploadDocsApacuanaUser: (data: any) => void;
  startFaceLiveness: () => void;
  match: any;
  apacuanaId: any;
  pendingDocuments: any;
  customer: any;
}

class RequestCert extends React.Component<Props> {
  render() {
    const {
      cert,
      step,
      handleNext,
      handleBack,
      apacuanaId,
      startFaceLiveness,
      pendingDocuments,
      customer,
    } = this.props;
    const { user: apacuanaUser } = this.props.apacuana || {};
    const useProfesionalInfo = cert?.id === 4;
    return (
      <div id='lawyer-validation'>
        <Link to='/' className='back'>
          <img src={BackIcon} />
        </Link>
        {step === 0 && <RequestCertForm cert={cert} handleNext={handleNext} />}
        {step === 1 && (
          <BasicDataCertificate
            cert={cert}
            handleNext={handleNext}
            handleBack={handleBack}
            apacuanaUser={apacuanaUser}
            useProfesionalInfo={useProfesionalInfo}
          />
        )}
        {step === 2 && (
          <UploadDocsCertificate
            cert={cert}
            handleNext={handleNext}
            handleBack={handleBack}
            apacuanaId={apacuanaId}
            pendingDocuments={pendingDocuments}
            apacuanaUser={customer}
          />
        )}
        {step === 3 && (
          <FaceLiveness
            cert={cert}
            handleBack={handleBack}
            handleNext={handleNext}
            startFaceLiveness={startFaceLiveness}
            pendingDocuments={pendingDocuments}
          />
        )}
      </div>
    );
  }
}
const RequestCertWithApacuana = (props: Props) => {
  const apacuanaContext: any = useApacuana();
  const [cert, setCert] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [customer, setCustomer] = useState<any>(undefined);
  const apacuanaId = props.match.params.id;
  const init = async () => {
    setLoading(true);
    try {
      if (apacuanaContext?.isInitialized) {
        const certTypesList = await apacuanaContext?.sdk?.getCertTypes();

        const certTypes = certTypesList?.data?.records || [];
        const currentCertType = certTypes.find(
          (el: any) => el.handler === CERTIFICATES_TYPES[apacuanaContext.user.level.name],
        );
        const certRequirements = await apacuanaContext?.sdk?.getCertRequerimentsByType({
          type: Number(currentCertType.id),
        });

        setCert({
          ...currentCertType,
          documents: certRequirements.data.records,
        });
        if (apacuanaId) {
          const customer = await apacuanaContext?.sdk?.getCustomer();

          setCustomer(customer?.data?.userData);
        }
      }
    } catch (e) {
      console.log({ e });
    } finally {
      setLoading(false);
    }
  };

  const getUser = async () => {
    const user = store.getState().user;

    if (user) {
      const res: any = await AuthService.checkUser({
        user_id: user?.id,
        withoutLoading: true,
      });
      if (res.user) {
        if (res.user.status == Constants.USER_STATUS.ACTIVE) {
          props.dispatch({
            type: 'SET_USER',
            payload: res.user,
          });
        } else {
          Globals.showError('Lo sentimos, su usuario ha sido suspendido');
          close();
        }
      } else {
        Globals.showError('Lo sentimos, su usuario ha sido eliminado');
        close();
      }
    }
  };

  const createApacuanaUser = async (data: any) => {
    setLoading(true);
    try {
      console.log(
        '📤 [Apacuana] Payload que se envía a createApacuanaUser:',
        JSON.stringify(data, null, 2),
      );
      const response = await apacuanaContext?.sdk?.createApacuanaUser(data);
      await apacuanaContext?.updateSdkConfig(response.data.entry.id, false);
      const customer = await apacuanaContext?.sdk?.getCustomer();
      await UserService.client.set_apacuana_id({
        apacuanaId: response.data.entry.id,
        userId: apacuanaContext?.user?.id,
      });
      await getUser();
      setCustomer(customer?.data?.userData);

      setStep(2);
    } catch (error: any) {
      console.error('❌ [Apacuana] Error completo en createApacuanaUser:', error);
      console.error(
        '❌ [Apacuana] Response data:',
        error?.response?.data ?? error?.data ?? 'sin respuesta',
      );
      Globals.showError(
        error?.response?.data?.message ??
          error?.message ??
          'Ocurrió un error al registrar sus datos en Apacuana. Verifique la información e intente nuevamente.',
      );
    } finally {
      setLoading(false);
    }
  };

  const uploadDocsApacuanaUser = async (data: any) => {
    setLoading(true);
    try {
      const fileAttributes = Object.keys(data)
        .filter((key: any) => key.startsWith('file-'))
        .reduce((obj: any, key: any) => {
          obj[key] = data[key];
          return obj;
        }, {});
      const fullDocument = apacuanaContext?.user?.full_document ?? '';
      const docSplit = fullDocument.split('-');
      if (docSplit.length < 2 || !docSplit[0] || !docSplit[1]) {
        Globals.showError('No se pudo leer el documento del usuario. Por favor contacte soporte.');
        setLoading(false);
        return;
      }
      fileAttributes.kinddoc = docSplit[0];
      fileAttributes.doc = docSplit[1];
      fileAttributes.typeuser = cert?.id;

      // Log de verificación: muestra todos los archivos que se enviarán a Apacuana en el Step 2
      console.log(
        '📤 [Apacuana - Step 2] Payload de documentos que se envía a uploadDocsApacuanaUser:',
      );
      console.log(
        '  → Archivos subidos:',
        Object.keys(fileAttributes).filter((k) => k.startsWith('file-')),
      );
      Object.keys(fileAttributes)
        .filter((k) => k.startsWith('file-'))
        .forEach((key) => {
          const file = fileAttributes[key];
          console.log(
            `  → ${key}:`,
            file instanceof File ? `${file.name} (${(file.size / 1024).toFixed(1)} KB)` : file,
          );
        });
      console.log('  → kinddoc:', fileAttributes.kinddoc);
      console.log('  → doc:', fileAttributes.doc);
      console.log('  → typeuser:', fileAttributes.typeuser);
      const response = await apacuanaContext?.sdk?.createApacuanaUser(fileAttributes);
      const customer = await apacuanaContext?.sdk?.getCustomer();

      setCustomer(customer?.data?.userData);
      if (isFaceLivenessComplete) {
        Globals.showSuccess(
          '¡Solicitud completada! Su certificado digital se encuentra en proceso de verificación.',
        );

        props.history.replace('/home');
      } else {
        setStep(3);
      }
    } catch (error: any) {
      console.error('Error al subir documentos a Apacuana:', error);
      Globals.showError(
        error?.message
          ? `Error Apacuana: ${error.message}`
          : 'Ocurrió un error al subir los documentos. Por favor intente nuevamente.',
      );
    } finally {
      setLoading(false);
    }
  };

  const startFaceLiveness = async () => {
    setLoading(true);
    try {
      // Log de verificación: muestra la URL de MediaPipe que se pasa al SDK en el Step 3
      console.log('📤 [Apacuana - Step 3] Iniciando verificación de vida (FaceLiveness)');
      console.log('  → URL de MediaPipe:', MEDIAPIPE_URL);
      // MEDIAPIPE_URL indica al SDK de Apacuana desde dónde descargar la librería
      // MediaPipe de Google, necesaria para el análisis facial en tiempo real.
      // Si Apacuana cambia este URL, se modifica solo en utils.ts y aplica aquí automáticamente.
      await apacuanaContext?.sdk?.startLivenessCheck(MEDIAPIPE_URL);

      Globals.showSuccess(
        '¡Solicitud completada! Su certificado digital se encuentra en proceso de emisión.',
      );

      props.history.replace('/home');
    } catch (error) {
      console.error('Error al iniciar o completar la prueba de vida:', error);

      Globals.showError('Error en la verificación de vida. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = (data?: any) => {
    if (step === 0) {
      setStep(1);
    }
    if (step === 1) {
      createApacuanaUser(data);
    }
    if (step === 2) {
      uploadDocsApacuanaUser(data);
    }
    if (step === 3) {
      setStep(4);
    }
    if (step === 4) {
      alert('final');
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const { pendingDocuments, isFaceLivenessComplete } = useMemo(() => {
    const customerDocs = customer?.documents || [];
    const requiredDocs = cert?.documents || [];
    const uploadedDocIds = new Set(
      customerDocs
        .filter((doc: any) => !doc.faceliveness && doc.pref?.id && doc.status.id !== -1)
        .map((doc: any) => doc.pref.id),
    );
    const pendingDocs = requiredDocs
      .filter((doc: any) => !doc.faceliveness) // Solo documentos no Liveness
      .filter((doc: any) => !uploadedDocIds.has(doc.id)); // El ID requerido no está en los cargados
    const isLivenessRequired = requiredDocs.some((doc: any) => doc.faceliveness);
    let livenessComplete = false;

    if (isLivenessRequired) {
      const unique = customerDocs.filter(
        (item: any, index: any, self: any) =>
          index === self.findIndex((t: any) => t.pref.id === item.pref.id),
      );

      livenessComplete = !unique.find(
        (doc: any) => doc.faceliveness && (doc.status?.id === -1 || doc.id === null),
      );
    }
    if (!isLivenessRequired) {
      livenessComplete = true;
    }

    return {
      pendingDocuments: pendingDocs,
      isFaceLivenessComplete: livenessComplete,
    };
  }, [cert, customer]);
  useEffect(() => {
    if (loading) {
      Globals.setLoading();
    } else {
      Globals.quitLoading();
    }
  }, [loading]);

  useEffect(() => {
    if (apacuanaId && apacuanaId !== '') {
      if (pendingDocuments.length === 0) {
        setStep(3);
      } else {
        setStep(2);
      }
    }
  }, [apacuanaId, pendingDocuments]);

  useEffect(() => {
    init();
  }, [apacuanaContext.user]);
  return (
    <RequestCert
      {...props}
      apacuana={apacuanaContext}
      cert={cert}
      step={step}
      handleNext={handleNext}
      handleBack={handleBack}
      startFaceLiveness={startFaceLiveness}
      apacuanaId={apacuanaId}
      pendingDocuments={pendingDocuments}
      customer={customer}
    />
  );
};

export default connect((state: RootState) => {
  return {
    user: state.user,
  };
})(RequestCertWithApacuana);
